'use client';

import { useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { MONTH_KEYS } from './calendar-utils';
import { monthGridClasses, monthCellClasses } from './MonthGrid.style';

export interface MonthGridProps {
  /** The currently selected month (0–11). */
  selectedMonth: number;
  /** The current calendar month (0–11), used to highlight "current" like today indicator. */
  currentMonth: number;
  /** Called when the user selects a month. */
  onSelect: (month: number) => void;
  /** The year currently being viewed (needed for minDate/maxDate month disabling). */
  viewYear?: number;
  /** Earliest selectable date. Months before this are disabled. */
  minDate?: Date;
  /** Latest selectable date. Months after this are disabled. */
  maxDate?: Date;
}

function isMonthDisabled(
  monthIndex: number,
  viewYear: number | undefined,
  minDate: Date | undefined,
  maxDate: Date | undefined
): boolean {
  if (viewYear == null) return false;
  if (minDate) {
    const minYear = minDate.getFullYear();
    const minMonth = minDate.getMonth();
    if (viewYear < minYear || (viewYear === minYear && monthIndex < minMonth)) {
      return true;
    }
  }
  if (maxDate) {
    const maxYear = maxDate.getFullYear();
    const maxMonth = maxDate.getMonth();
    if (viewYear > maxYear || (viewYear === maxYear && monthIndex > maxMonth)) {
      return true;
    }
  }
  return false;
}

/**
 * 4×3 month grid sub-component.
 * Shows JANUARY through DECEMBER with selected/current highlighting.
 * Full keyboard navigation: arrow keys, Enter/Space to select.
 */
export function MonthGrid({
  selectedMonth,
  currentMonth,
  onSelect,
  viewYear,
  minDate,
  maxDate,
}: MonthGridProps) {
  const t = useTranslations('DatePicker');
  const gridRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const index = parseInt(target.getAttribute('data-index') ?? '-1', 10);
      if (index < 0) return;

      let nextIndex: number | null = null;
      const COLS = 3;

      switch (e.key) {
        case 'ArrowLeft':
          nextIndex = index > 0 ? index - 1 : index;
          break;
        case 'ArrowRight':
          nextIndex = index < 11 ? index + 1 : index;
          break;
        case 'ArrowUp':
          nextIndex = index - COLS >= 0 ? index - COLS : index;
          break;
        case 'ArrowDown':
          nextIndex = index + COLS <= 11 ? index + COLS : index;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = 11;
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!isMonthDisabled(index, viewYear, minDate, maxDate)) {
            onSelect(index);
          }
          return;
        default:
          return;
      }

      e.preventDefault();
      if (nextIndex !== null) {
        const cell = gridRef.current?.querySelector<HTMLElement>(`[data-index="${nextIndex}"]`);
        cell?.focus();
      }
    },
    [onSelect, viewYear, minDate, maxDate]
  );

  return (
    <div
      ref={gridRef}
      className={monthGridClasses}
      role="grid"
      aria-label={t('selectMonth')}
      onKeyDown={handleKeyDown}
    >
      {/* Render 4 rows × 3 cols */}
      {[0, 1, 2, 3].map((rowIdx) => (
        <div key={rowIdx} role="row" className="tw:contents">
          {[0, 1, 2].map((colIdx) => {
            const monthIndex = rowIdx * 3 + colIdx;
            const isSelected = monthIndex === selectedMonth;
            const isCurrent = monthIndex === currentMonth;
            const isDisabled = isMonthDisabled(monthIndex, viewYear, minDate, maxDate);

            let state: 'default' | 'selected' | 'current' | 'disabled';
            if (isDisabled) {
              state = 'disabled';
            } else if (isSelected) {
              state = 'selected';
            } else if (isCurrent) {
              state = 'current';
            } else {
              state = 'default';
            }

            return (
              <button
                key={monthIndex}
                type="button"
                role="gridcell"
                aria-selected={isSelected}
                aria-disabled={isDisabled}
                aria-label={t(`months.${MONTH_KEYS[monthIndex]}` as Parameters<typeof t>[0])}
                tabIndex={isSelected ? 0 : -1}
                data-index={monthIndex}
                data-cy="calendar-month"
                onClick={() => !isDisabled && onSelect(monthIndex)}
                className={monthCellClasses({ state })}
              >
                {t(`months.${MONTH_KEYS[monthIndex]}` as Parameters<typeof t>[0]).toUpperCase()}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
